import type { WarriorDefinition, WarriorDataFile } from '@/types';

const getBaseUrl = () => import.meta.env.BASE_URL;
const getWarriorsUrl = () => `${getBaseUrl()}data/warriors.json`;

/**
 * Fix asset paths by prepending base URL
 */
function fixAssetPath(path: string): string {
  // Remove leading slash if present, then prepend base URL
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${getBaseUrl()}${cleanPath}`;
}

/**
 * Service for loading warrior definitions
 */
export class WarriorLoader {
  private static cache: WarriorDefinition[] | null = null;

  /**
   * Load warrior definitions from the JSON file
   */
  static async loadWarriors(): Promise<WarriorDefinition[]> {
    if (this.cache) {
      return this.cache;
    }

    try {
      const response = await fetch(getWarriorsUrl());
      if (!response.ok) {
        throw new Error(`Failed to load warriors: ${response.statusText}`);
      }

      const data: WarriorDataFile = await response.json();

      // Validate the data
      if (!data.warriors || !Array.isArray(data.warriors)) {
        throw new Error('Invalid warrior data format');
      }

      // Fix image paths to include base URL
      const warriors = data.warriors.map(warrior => ({
        ...warrior,
        images: {
          sprite: fixAssetPath(warrior.images.sprite),
        },
      }));

      this.cache = warriors;
      return this.cache;
    } catch (error) {
      console.error('Error loading warriors:', error);
      throw error;
    }
  }

  /**
   * Get a warrior definition by ID
   */
  static async getWarrior(id: string): Promise<WarriorDefinition | undefined> {
    const warriors = await this.loadWarriors();
    return warriors.find(w => w.id === id);
  }

  /**
   * Clear the cache (useful for testing)
   */
  static clearCache(): void {
    this.cache = null;
  }
}
